import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import LanguageSelector from '../components/LanguageSelector';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = ({ setIsAuthenticated }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [loginData, setLoginData] = useState({ operator_id: '', password: '' });
  const [registerData, setRegisterData] = useState({
    operator_id: '',
    password: '',
    name: '',
    email: '',
    coupon_code: '',
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Handle Google OAuth callback
  useEffect(() => {
    const hash = location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        processGoogleSession(sessionId);
      }
    }
  }, [location]);

  const processGoogleSession = async (sessionId) => {
    setGoogleLoading(true);
    try {
      const response = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
      localStorage.setItem('auth_token', response.data.access_token);
      localStorage.setItem('operator_name', response.data.name);
      localStorage.setItem('profile_picture', response.data.profile_picture || '');
      toast.success(t('auth.googleLoginSuccess') || 'Signed in with Google successfully!');
      setIsAuthenticated(true);
      // Clear the hash and navigate
      window.history.replaceState(null, '', window.location.pathname);
      navigate('/app/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Google sign-in failed');
      window.history.replaceState(null, '', window.location.pathname);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      localStorage.setItem('auth_token', response.data.access_token);
      localStorage.setItem('operator_name', response.data.name);
      toast.success(t('auth.loginSuccess'));
      setIsAuthenticated(true);
      navigate('/app/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!acceptedTerms) {
      toast.error(t('auth.acceptTermsError'));
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, registerData);
      localStorage.setItem('auth_token', response.data.access_token);
      localStorage.setItem('operator_name', response.data.name);
      localStorage.setItem('terms_accepted', new Date().toISOString());
      
      if (response.data.subscription_status === 'active') {
        toast.success(t('auth.couponApplied'));
      } else {
        toast.success(t('auth.registerSuccess'));
      }
      
      setIsAuthenticated(true);
      navigate('/app/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 h-screen w-full">
      <div className="flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <div className="flex flex-col items-start">
                <img 
                  src="/tourpilot-logo.png" 
                  alt="TourPilot.biz" 
                  className="h-16 max-w-[250px] object-contain"
                  data-testid="login-title"
                />
                <p className="text-xs text-muted-foreground mt-1 whitespace-nowrap">{t('landing.tagline')}</p>
              </div>
              <LanguageSelector />
            </div>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid="login-tab">{t('auth.login')}</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">{t('auth.register')}</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <Card>
                <CardHeader>
                  <CardTitle>{t('auth.welcomeBack')}</CardTitle>
                  <CardDescription>{t('auth.loginToAccount')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-operator-id">{t('auth.emailOrOperatorId')}</Label>
                      <Input
                        id="login-operator-id"
                        data-testid="login-operator-id-input"
                        placeholder={t('auth.enterEmailOrId')}
                        value={loginData.operator_id}
                        onChange={(e) => setLoginData({ ...loginData, operator_id: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">{t('auth.password')}</Label>
                      <Input
                        id="login-password"
                        data-testid="login-password-input"
                        type="password"
                        placeholder={t('auth.enterPassword')}
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full" data-testid="login-submit-button" disabled={loading}>
                      {loading ? t('auth.loggingIn') : t('auth.login')}
                    </Button>
                    
                    {/* Divider */}
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">{t('auth.orContinueWith') || 'Or continue with'}</span>
                      </div>
                    </div>
                    
                    {/* Google Sign-In Button */}
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="w-full" 
                      onClick={handleGoogleSignIn}
                      disabled={googleLoading}
                    >
                      {googleLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      )}
                      {googleLoading ? (t('auth.signingIn') || 'Signing in...') : (t('auth.continueWithGoogle') || 'Continue with Google')}
                    </Button>
                    
                    <div className="text-center mt-4">
                      <a href="/forgot-password" className="text-sm text-primary hover:underline">
                        {t('auth.forgotPassword')}
                      </a>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="register">
              <Card>
                <CardHeader>
                  <CardTitle>{t('auth.createAccount')}</CardTitle>
                  <CardDescription>{t('auth.registerAsNew')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">{t('auth.name')}</Label>
                      <Input
                        id="register-name"
                        data-testid="register-name-input"
                        placeholder={t('auth.enterName')}
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">{t('auth.email')}</Label>
                      <Input
                        id="register-email"
                        data-testid="register-email-input"
                        type="email"
                        placeholder={t('auth.enterEmail')}
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-operator-id">{t('auth.operatorId')}</Label>
                      <Input
                        id="register-operator-id"
                        data-testid="register-operator-id-input"
                        placeholder={t('auth.chooseOperatorId')}
                        value={registerData.operator_id}
                        onChange={(e) => setRegisterData({ ...registerData, operator_id: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">{t('auth.password')}</Label>
                      <Input
                        id="register-password"
                        data-testid="register-password-input"
                        type="password"
                        placeholder={t('auth.createPassword')}
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-coupon">{t('auth.couponOptional')}</Label>
                      <Input
                        id="register-coupon"
                        data-testid="register-coupon-input"
                        placeholder={t('auth.enterCoupon')}
                        value={registerData.coupon_code}
                        onChange={(e) => setRegisterData({ ...registerData, coupon_code: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground">{t('auth.tryCoupon')}</p>
                    </div>
                    <div className="flex items-start space-x-3 pt-2">
                      <Checkbox 
                        id="terms" 
                        checked={acceptedTerms}
                        onCheckedChange={(checked) => setAcceptedTerms(checked)}
                        data-testid="terms-checkbox"
                      />
                      <label 
                        htmlFor="terms" 
                        className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
                      >
                        {t('auth.acceptTerms')}{' '}
                        <Link to="/terms-of-service" className="text-primary hover:underline font-medium" target="_blank">
                          {t('common.terms')}
                        </Link>
                        {' '}{t('common.and')}{' '}
                        <Link to="/privacy-policy" className="text-primary hover:underline font-medium" target="_blank">
                          {t('common.privacy')}
                        </Link>
                      </label>
                    </div>
                    <Button type="submit" className="w-full" data-testid="register-submit-button" disabled={loading || !acceptedTerms}>
                      {loading ? t('auth.registering') : t('auth.register')}
                    </Button>
                    
                    {/* Divider */}
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">{t('auth.orContinueWith') || 'Or continue with'}</span>
                      </div>
                    </div>
                    
                    {/* Google Sign-Up Button */}
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="w-full" 
                      onClick={handleGoogleSignIn}
                      disabled={googleLoading}
                    >
                      {googleLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      )}
                      {googleLoading ? (t('auth.signingUp') || 'Signing up...') : (t('auth.signUpWithGoogle') || 'Sign up with Google')}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <div className="hidden lg:flex relative bg-muted">
        <img
          src="https://images.unsplash.com/photo-1755702819364-50df59f0f547?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxzY2VuaWMlMjB0cmF2ZWwlMjBsYW5kc2NhcGUlMjBtb3VudGFpbnMlMjBiZWFjaHxlbnwwfHx8fDE3NjY0MTM5NDR8MA&ixlib=rb-4.1.0&q=85"
          alt="Scenic travel landscape"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/20 flex items-end p-12">
          <div className="backdrop-blur-lg bg-white/10 p-8 rounded-2xl border border-white/20">
            <blockquote className="text-white">
              <p className="text-xl font-heading font-semibold mb-2">
                &ldquo;{t('auth.manageWithEase')}&rdquo;
              </p>
              <p className="text-sm text-white/80">{t('auth.trackAll')}</p>
            </blockquote>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
